import * as React from 'react';
import { Empty } from 'antd';
import withStyles from 'react-jss';
import { capitalize, formatDateTime, sanitizeDateString } from 'src/utils/utils';
import { CssType, ThemeType } from 'src/theme/jss-types';

const styles = (theme: ThemeType): CssType => ({
  container: {
    padding: '20px',
    backgroundColor: 'white',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px 0',
  },
  item: {
    alignItems: 'baseline',
    width: '25%',
  },
  label: {
    fontSize: '14px',
    fontWeight: 400,
    color: '#262626',
    minWidth: 'fit-content',
  },
  value: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#000',
    wordBreak: 'break-word',
  },
  valueNA: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#BFBFBF',
    wordBreak: 'break-word',
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
  },
});

interface ComputationHeader {
  column_id: string;
  pretty_name: string;
}

interface InputParametersProps {
  data: Record<string, any>;
  computationHeaders: ComputationHeader[];
  classes: Record<string, string>;
}

const formatValue = (key: string, value: any): string => {
  if (key === 'event_time' && value) {
    return formatDateTime(sanitizeDateString(value), 'DD MMM YYYY HH:mm');
  }

  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return 'N/A';
    return value
      .map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)))
      .join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const InputParameters: React.FC<InputParametersProps> = ({
  data,
  computationHeaders,
  classes,
}) => {
  const filteredFields = React.useMemo(() => {
    if (computationHeaders?.length === 0) {
      return Object.entries(data || {}).map(([key, value]) => ({
        key,
        label: capitalize(key),
        value,
      }));
    }

    return computationHeaders
      .filter((header) => data && Object.hasOwn(data, header.column_id))
      .map((header) => ({
        key: header.column_id,
        label: header.pretty_name,
        value: data[header.column_id],
      }));
  }, [data, computationHeaders]);

  if (filteredFields.length === 0) {
    return (
      <div className={classes.container}>
        <div className={classes.empty}>
          <Empty description="No input parameters to display" />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.grid}>
        {filteredFields.map((field) => {
          const formattedValue = formatValue(field.key, field.value);

          return (
            <div key={field.key} className={classes.item}>
              <div className={classes.label}>{field.label}:</div>
              <div className={formattedValue === 'N/A' ? classes.valueNA : classes.value}>
                {formattedValue}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default withStyles(styles, { injectTheme: true })(InputParameters);
